import { getStorytellerApiKey } from "./config.ts";
let activeMedia = null;
export function toExactClipSource(clipId) {
    const normalizedClipId = clipId.trim();
    if (!normalizedClipId) {
        throw new Error("A Storyteller Clip ID is required.");
    }
    return Object.freeze({ clipId: normalizedClipId });
}
export function watchMediaPlayback(media) {
    let watching = true;
    let claimVersion = 0;
    const release = () => {
        claimVersion += 1;
        if (activeMedia === media) {
            activeMedia = null;
        }
    };
    const onPlaying = () => {
        const version = ++claimVersion;
        // The media play promise is resolved after `playing` dispatch returns.
        // Defer arbitration so pausing the previous winner cannot reject either
        // participant's still-pending play promise with AbortError.
        queueMicrotask(() => {
            if (!watching || version !== claimVersion) {
                return;
            }
            const previousMedia = activeMedia;
            activeMedia = media;
            if (previousMedia && previousMedia !== media) {
                previousMedia.pause();
            }
        });
    };
    media.addEventListener("playing", onPlaying);
    media.addEventListener("pause", release);
    media.addEventListener("ended", release);
    media.addEventListener("emptied", release);
    media.addEventListener("error", release);
    return () => {
        watching = false;
        release();
        media.removeEventListener("playing", onPlaying);
        media.removeEventListener("pause", release);
        media.removeEventListener("ended", release);
        media.removeEventListener("emptied", release);
        media.removeEventListener("error", release);
    };
}
function pauseLocalFeedMedia() {
    if (typeof document === "undefined") {
        return;
    }
    for (const media of document.querySelectorAll(".feed-card__media video, .feed-card__media audio")) {
        if (!media.paused) {
            media.pause();
        }
    }
}
export function createStorytellerRuntime(dependencies) {
    let initialization = null;
    let initializedSdk = null;
    let storyReturnPoint = null;
    const restoreStoryReturnPoint = () => {
        const returnPoint = storyReturnPoint;
        storyReturnPoint = null;
        if (!returnPoint || typeof window === "undefined") {
            return;
        }
        window.requestAnimationFrame(() => {
            if (returnPoint.feed?.isConnected) {
                returnPoint.feed.scrollTop = returnPoint.feedScrollTop;
            }
            window.scrollTo({ top: returnPoint.pageScrollY, behavior: "auto" });
            if (returnPoint.launcher.isConnected) {
                returnPoint.launcher.focus({ preventScroll: true });
            }
        });
    };
    const installDelegate = (sdk) => {
        const previousDelegate = sdk.sharedInstance.delegate;
        sdk.sharedInstance.delegate = {
            ...previousDelegate,
            onUserActivityOccurred: (type, data) => {
                previousDelegate?.onUserActivityOccurred?.(type, data);
                if (sdk.clipPlaybackActivities.has(type)) {
                    pauseLocalFeedMedia();
                }
                if (type === sdk.dismissedStoryActivity) {
                    restoreStoryReturnPoint();
                }
            }
        };
    };
    const initialize = () => {
        if (initialization) {
            return initialization;
        }
        initialization = (async () => {
            const apiKey = dependencies.getApiKey();
            const sdk = await dependencies.loadSdk();
            if (!sdk.sharedInstance.isInitialized) {
                await sdk.sharedInstance.initialize(apiKey);
            }
            installDelegate(sdk);
            initializedSdk = sdk;
            return sdk;
        })().catch((error) => {
            initialization = null;
            initializedSdk = null;
            throw error;
        });
        return initialization;
    };
    const createEmbeddedClip = async (elementId, clipId, delegate) => {
        const sdk = await initialize();
        const source = toExactClipSource(clipId);
        const view = sdk.createEmbeddedClip(elementId, source);
        view.delegate = delegate;
        return {
            view,
            dispose: () => {
                view.delegate = {};
                if (typeof document === "undefined") {
                    return;
                }
                const mount = document.getElementById(elementId);
                for (const media of mount?.querySelectorAll("video, audio") ?? []) {
                    media.pause();
                }
                mount?.replaceChildren();
            }
        };
    };
    const openStory = async (storyId, launcher) => {
        const normalizedStoryId = storyId.trim();
        if (!normalizedStoryId) {
            throw new Error("A Storyteller Story ID is required.");
        }
        const sdk = await initialize();
        const phone = launcher.closest(".game-phone");
        const feed = phone?.querySelector(".game-feed") ?? null;
        storyReturnPoint = {
            launcher,
            feed,
            feedScrollTop: feed?.scrollTop ?? 0,
            pageScrollY: typeof window === "undefined" ? 0 : window.scrollY
        };
        try {
            await sdk.sharedInstance.openStory(normalizedStoryId);
        }
        catch (error) {
            restoreStoryReturnPoint();
            throw error;
        }
    };
    const releaseStoryLauncher = (launcher) => {
        if (storyReturnPoint?.launcher !== launcher) {
            return;
        }
        initializedSdk?.sharedInstance.dismissPlayer(false);
        restoreStoryReturnPoint();
    };
    return {
        initialize,
        createEmbeddedClip,
        openStory,
        releaseStoryLauncher
    };
}
async function loadStorytellerSdk() {
    const [sdk] = await Promise.all([
        import("@getstoryteller/storyteller-sdk-javascript"),
        import("@getstoryteller/storyteller-sdk-javascript/dist/storyteller.min.css")
    ]);
    return {
        sharedInstance: sdk.sharedInstance,
        dismissedStoryActivity: sdk.ActivityType.dismissedStory,
        clipPlaybackActivities: new Set([
            sdk.ActivityType.openedClip,
            sdk.ActivityType.resumedClip,
            sdk.ActivityType.mediaStarted
        ]),
        createEmbeddedClip: (elementId, source) => new sdk.StorytellerEmbeddedClipsPlayerView(elementId, source)
    };
}
export const storytellerRuntime = createStorytellerRuntime({
    getApiKey: getStorytellerApiKey,
    loadSdk: loadStorytellerSdk
});
